import {useCallback, useState} from "react";
import dayjs from "dayjs";
import {Box, Button, ClickAwayListener, Collapse, Paper, Stack, SxProps, Typography} from "@mui/material";
import CalendarIcon from "@mui/icons-material/CalendarTodayOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {DateCalendar} from "@mui/x-date-pickers";
import {useLoadableValueOrDefault} from "../../hooks/jotaiHooks";
import {activeEntityDateOptionsLoadable} from "../../store/activeDateStore";
import {formatLongDate} from "../../utils/dateUtils";
import {useUserPreferences} from "../../hooks/userPreferencesHooks";
import {useShowAllCalendarDatesFeatureFlag} from "../../hooks/featureFlagsHook";

export interface Props {
    date: dayjs.Dayjs | null;
    onDateChange?: (date: dayjs.Dayjs | null) => void;
    sx?: SxProps;
    disableElevation?: boolean;
    disabled: boolean;
}

export const ResultsDatePicker: React.FC<Props> = (props) => {
    const {dateFormat} = useUserPreferences();
    const {date, onDateChange, sx, disabled, disableElevation = false} = props;
    const [opened, setOpened] = useState(false);

    const showAllDatesFeatureFlag = useShowAllCalendarDatesFeatureFlag();

    const dateOptions: dayjs.Dayjs[] = useLoadableValueOrDefault(activeEntityDateOptionsLoadable, []);
    const shouldDisabledate = useCallback(
        (date: dayjs.Dayjs) => {
            if (showAllDatesFeatureFlag !== false && date.startOf("day") < dayjs().startOf("day")) return false;

            return !dateOptions.find((option) => option.isSame(date));
        },
        [dateOptions, showAllDatesFeatureFlag],
    );

    const onChange = (date: dayjs.Dayjs | null) => {
        setOpened(false);
        if (onDateChange) {
            onDateChange(date);
        }
    };

    return (
        <ClickAwayListener onClickAway={() => setOpened(false)}>
            <Box sx={sx}>
                <Button
                    variant="contained"
                    disableElevation={disableElevation}
                    color="secondary"
                    disabled={disabled || date === null}
                    onClick={() => setOpened(!opened)}
                    sx={{
                        height: "2rem",
                        width: "100%",
                        padding: "0.7rem",
                        "&.Mui-disabled": {
                            background: "#F3F5FA",
                        },
                    }}
                >
                    <Stack direction="row" gap="0.7rem" width="100%" justifyContent="space-between">
                        <Stack direction="row" gap="0.7rem">
                            <CalendarIcon sx={{width: "1.1rem"}} />
                            <Typography textTransform="none" color={disabled ? "#8F94A8" : "#3A3F4F"} fontSize={16}>
                                {date ? formatLongDate(date, dateFormat) : ""}
                            </Typography>
                        </Stack>
                        {opened ? (
                            <ExpandLessIcon
                                sx={{
                                    width: "1.4rem",
                                    height: "1.4rem",
                                    color: "#8F94A8",
                                }}
                            />
                        ) : (
                            <ExpandMoreIcon
                                sx={{
                                    width: "1.4rem",
                                    height: "1.4rem",
                                    color: "#8F94A8",
                                }}
                            />
                        )}
                    </Stack>
                </Button>
                <Collapse in={opened} sx={{position: "absolute", minWidth: "15rem", zIndex: 100}}>
                    <Stack component={Paper} elevation={2} square sx={{borderRadius: "0.25rem", bgcolor: "white"}}>
                        <DateCalendar value={date} onChange={onChange} shouldDisableDate={shouldDisabledate} />
                    </Stack>
                </Collapse>
            </Box>
        </ClickAwayListener>
    );
};
